import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Upload, Loader2, FileText, AlertTriangle, CheckCircle2, Trash2, Copy,
  ArrowLeft, Wand2, AlertCircle,
} from "lucide-react";
import AdminShell from "../components/AdminShell";
import { useI18n } from "../i18n/I18nContext";
import { api } from "../lib/nailApi";
import { Toast } from "../components/AdminUI";

const TOKEN_KEY = "nailEvent.adminToken";

const ACCEPT = ".csv,.json,.md,.txt,.pdf,.docx,.pptx,.zip";
const SUPPORTED_LABEL = "CSV · JSON · MD · TXT · PDF · DOCX · PPTX · ZIP";

export default function AdminKnowledgeImport() {
  const { lang } = useI18n();
  const navigate = useNavigate();
  const token = sessionStorage.getItem(TOKEN_KEY) || "";
  const L = (vi, en) => (lang === "en" ? en : vi);
  const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);

  const [jobs, setJobs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [rows, setRows] = useState([]); // editable preview rows
  const [skipDups, setSkipDups] = useState(true);
  const [committing, setCommitting] = useState(false);
  const [toast, setToast] = useState("");
  const [drag, setDrag] = useState(false);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2200); };

  const loadJobs = async () => {
    try {
      const r = await api.get("/admin/knowledge/import", { headers });
      setJobs(r.data.jobs || []);
    } catch (_err) { /* ignore */ }
  };

  useEffect(() => { loadJobs(); }, []);

  const onFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setCurrentJob(null);
    setRows([]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/knowledge/import/upload", fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setCurrentJob(r.data);
      setRows(r.data.preview.map((p) => ({
        ...p,
        include: p.errors?.length === 0 && (!skipDups || p.duplicates?.length === 0),
        // Phase 7.3 — default target_lifecycle by AI priority. Urgent → under_review.
        target_lifecycle: (p.review_priority === "urgent" || p.review_priority === "high") ? "under_review" : "draft",
        // Editable fields seeded from suggestions
        category: p.suggested?.category || "",
        subcategory: p.suggested?.subcategory || "",
        jurisdiction: p.suggested?.jurisdiction || "",
        compliance_severity: p.suggested?.compliance_severity || "none",
        disclaimer_level: p.suggested?.disclaimer_level || "educational",
        business_types: p.suggested?.business_types || ["all"],
        language: p.suggested?.language || "vi",
        tags: (p.suggested?.tags || []).join(", "),
      })));
      flash(L(`Đã phân tích ${r.data.total_rows} dòng`, `Parsed ${r.data.total_rows} rows`));
      loadJobs();
    } catch (err) {
      flash(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const commit = async () => {
    if (!currentJob) return;
    const selected = rows.filter((r) => r.include);
    if (selected.length === 0) { flash(L("Vui lòng chọn ít nhất 1 dòng", "Select at least 1 row")); return; }
    setCommitting(true);
    try {
      const payload = {
        skip_duplicates: skipDups,
        rows: selected.map((r) => ({
          row_index: r.row_index,
          title: r.title,
          summary: r.summary,
          content: r.content,
          source: r.source,
          source_url: r.source_url,
          doc_type: r.doc_type,
          category: r.category || null,
          subcategory: r.subcategory || null,
          jurisdiction: r.jurisdiction || null,
          compliance_severity: r.compliance_severity || null,
          disclaimer_level: r.disclaimer_level || null,
          source_type: r.suggested?.source_type || null,
          business_types: r.business_types || [],
          language: r.language || "vi",
          tags: typeof r.tags === "string" ? r.tags.split(",").map((s) => s.trim()).filter(Boolean) : (r.tags || []),
          target_lifecycle: r.target_lifecycle || "draft",
          // Phase 7.3 — classification trail
          classification_source: r.classification_source,
          classification_confidence: r.classification_confidence,
          classification_reasoning: r.classification_reasoning,
          classification_warnings: r.classification_warnings,
          review_priority: r.review_priority,
          ai_suggested_metadata: r.ai_suggested_metadata,
        })),
      };
      const resp = await api.post(`/admin/knowledge/import/${currentJob.job_id}/commit`, payload, { headers });
      flash(L(`Đã thêm ${resp.data.created_count} tài liệu (bỏ ${resp.data.skipped_count})`, `Created ${resp.data.created_count} (skipped ${resp.data.skipped_count})`));
      setCurrentJob(null); setRows([]);
      loadJobs();
    } catch (err) {
      flash(err?.response?.data?.detail || "Commit failed");
    } finally {
      setCommitting(false);
    }
  };

  const discardJob = async () => {
    if (!currentJob) return;
    try {
      await api.delete(`/admin/knowledge/import/${currentJob.job_id}`, { headers });
      setCurrentJob(null); setRows([]);
      loadJobs();
      flash(L("Đã hủy", "Discarded"));
    } catch (_err) { /* ignore */ }
  };

  const updateRow = (idx, patch) => setRows((rs) => rs.map((r, i) => i === idx ? { ...r, ...patch } : r));

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) onFile(file);
  };

  return (
    <AdminShell
      testid="admin-knowledge-import"
      subtitle={L("Trí Tuệ", "Knowledge")}
      title={L("Nhập Tri thức Hàng loạt", "Bulk Knowledge Import")}
      backTo="/admin/library/knowledge"
    >
      <div className="max-w-[1400px] space-y-6">
        <Toast message={toast} testId="kbi-toast" />

        {/* Upload zone */}
        {!currentJob && (
          <div
            data-testid="kbi-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={onDrop}
            className={`bg-white border-2 border-dashed rounded-3xl p-12 text-center transition ${drag ? "border-espresso bg-linen-100" : "border-espresso/20"}`}>
            <Upload className="w-10 h-10 mx-auto text-espresso-muted" />
            <h2 className="font-serif text-2xl text-espresso mt-3">{L("Tải lên tệp tri thức", "Upload a knowledge file")}</h2>
            <p className="text-sm text-espresso-soft mt-1">{L("Kéo thả vào đây hoặc bấm để chọn", "Drag-and-drop or click to choose")}</p>
            <p className="text-[11px] text-espresso-muted mt-2">{L("Hỗ trợ:", "Supported:")} {SUPPORTED_LABEL}</p>
            <label className="mt-5 inline-flex items-center gap-2 bg-espresso text-linen rounded-full px-5 py-2.5 text-sm font-medium hover:bg-espresso-hover cursor-pointer" data-testid="kbi-choose-file">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? L("Đang tải...", "Uploading...") : L("Chọn tệp", "Choose file")}
              <input type="file" accept={ACCEPT} className="hidden" disabled={uploading}
                onChange={(e) => onFile(e.target.files?.[0])} data-testid="kbi-file-input" />
            </label>
          </div>
        )}

        {/* Preview table */}
        {currentJob && (
          <div className="bg-white border border-espresso/10 rounded-3xl overflow-hidden">
            <div className="px-5 py-4 border-b border-espresso/10 flex flex-wrap items-center gap-3">
              <FileText className="w-5 h-5 text-espresso" />
              <div className="flex-1 min-w-0">
                <div className="font-serif text-lg text-espresso truncate" data-testid="kbi-job-filename">{currentJob.filename}</div>
                <div className="text-xs text-espresso-muted">
                  {L("Định dạng:", "Type:")} <strong>{currentJob.file_type}</strong> ·
                  {L(" Tổng:", " Total:")} <strong>{currentJob.total_rows}</strong> ·
                  {L(" Trùng:", " Duplicates:")} <strong>{currentJob.duplicate_count}</strong> ·
                  {L(" Lỗi:", " Errors:")} <strong>{currentJob.failed_rows}</strong>
                </div>
              </div>
              <label className="text-xs text-espresso flex items-center gap-1.5 cursor-pointer" data-testid="kbi-skip-dups">
                <input type="checkbox" checked={skipDups} onChange={(e) => setSkipDups(e.target.checked)} />
                {L("Bỏ qua trùng lặp", "Skip duplicates")}
              </label>
              <button type="button" onClick={discardJob} className="text-xs text-rose-600 hover:underline inline-flex items-center gap-1" data-testid="kbi-discard">
                <Trash2 className="w-3.5 h-3.5" /> {L("Hủy", "Discard")}
              </button>
              <button type="button" disabled={committing} onClick={commit}
                className="inline-flex items-center gap-2 bg-espresso text-linen rounded-full px-5 py-2 text-sm font-medium hover:bg-espresso-hover disabled:opacity-60" data-testid="kbi-commit">
                {committing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {L("Xác nhận lưu", "Commit selected")}
              </button>
            </div>

            {currentJob.errors?.length > 0 && (
              <div className="px-5 py-2 bg-rose-50 border-b border-rose-200 text-xs text-rose-800" data-testid="kbi-job-errors">
                <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
                {currentJob.errors.join(" · ")}
              </div>
            )}

            <div className="divide-y divide-espresso/10" data-testid="kbi-preview-rows">
              {rows.map((r, idx) => (
                <ImportRow key={idx} row={r} idx={idx} L={L}
                  onChange={(patch) => updateRow(idx, patch)} />
              ))}
            </div>
          </div>
        )}

        {/* Recent jobs */}
        <div className="bg-white border border-espresso/10 rounded-3xl p-5">
          <h3 className="font-serif text-lg text-espresso mb-3">{L("Công việc gần đây", "Recent jobs")}</h3>
          {jobs.length === 0 ? (
            <p className="text-sm text-espresso-muted">{L("Chưa có lần nhập nào.", "No imports yet.")}</p>
          ) : (
            <table className="w-full text-sm" data-testid="kbi-jobs-table">
              <thead className="text-left text-[11px] uppercase tracking-[0.16em] text-espresso-muted border-b border-espresso/10">
                <tr><th className="py-2">ID</th><th>{L("Tệp", "File")}</th><th>{L("Loại", "Type")}</th><th>{L("Trạng thái", "Status")}</th><th>{L("Dòng", "Rows")}</th><th>{L("Đã lưu", "Created")}</th><th>{L("Ngày", "When")}</th></tr>
              </thead>
              <tbody className="divide-y divide-espresso/5">
                {jobs.map((j) => (
                  <tr key={j.id} data-testid={`kbi-job-row-${j.id}`}>
                    <td className="py-2 font-mono text-xs">#{j.id}</td>
                    <td className="truncate max-w-[260px]">{j.filename}</td>
                    <td><code className="text-xs bg-linen-100 rounded px-1.5 py-0.5">{j.file_type}</code></td>
                    <td><StatusBadge status={j.status} L={L} /></td>
                    <td>{j.total_rows}</td>
                    <td className="text-emerald2 font-medium">{j.succeeded_rows}</td>
                    <td className="text-xs text-espresso-muted">{j.created_at ? new Date(j.created_at).toLocaleString() : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminShell>
  );
}

function StatusBadge({ status, L }) {
  const map = {
    previewed: { cls: "bg-amber-100 text-amber-800", vi: "Chờ duyệt", en: "Previewed" },
    committed: { cls: "bg-emerald2/10 text-emerald2", vi: "Đã lưu", en: "Committed" },
    failed:    { cls: "bg-rose-100 text-rose-800", vi: "Lỗi", en: "Failed" },
    cancelled: { cls: "bg-warmgray-100 text-warmgray-700", vi: "Đã hủy", en: "Cancelled" },
    pending:   { cls: "bg-warmgray-100 text-warmgray-700", vi: "Đang chờ", en: "Pending" },
  }[status] || { cls: "bg-warmgray-100 text-warmgray-700", vi: status, en: status };
  return <span className={`text-[10px] uppercase tracking-[0.14em] font-semibold rounded-full px-2 py-0.5 ${map.cls}`}>{L(map.vi, map.en)}</span>;
}

function ImportRow({ row: r, idx, L, onChange }) {
  const hasErr = (r.errors || []).length > 0;
  const hasDup = (r.duplicates || []).length > 0;
  const isAi = r.classification_source === "ai";
  const isFallback = r.classification_source === "deterministic_fallback";
  const conf = r.classification_confidence || 0;
  const lowConf = isAi && conf > 0 && conf < 60;
  const prio = r.review_priority || "normal";
  const isUrgent = prio === "urgent";
  const isHigh = prio === "high";
  const hasWarnings = (r.classification_warnings || []).length > 0;

  return (
    <div className={`px-5 py-4 ${hasErr ? "bg-rose-50" : isUrgent ? "bg-rose-50/40" : hasDup ? "bg-amber-50/50" : ""}`} data-testid={`kbi-row-${idx}`}>
      <div className="flex items-start gap-3">
        <input type="checkbox" checked={!!r.include} onChange={(e) => onChange({ include: e.target.checked })}
          data-testid={`kbi-row-include-${idx}`} className="mt-1.5" />
        <div className="flex-1 min-w-0">
          <input value={r.title || ""} onChange={(e) => onChange({ title: e.target.value })} data-testid={`kbi-row-title-${idx}`}
            className="font-serif text-base text-espresso w-full bg-transparent border-b border-transparent hover:border-espresso/20 focus:border-espresso/40 focus:outline-none" />
          {r.summary && <p className="text-xs text-espresso-soft mt-0.5 line-clamp-2">{r.summary}</p>}

          {/* Phase 7.3 — Classification badges */}
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {isAi && (
              <span data-testid={`kbi-badge-ai-${idx}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-semibold rounded-full px-2 py-0.5 bg-emerald2/10 text-emerald2">
                <Wand2 className="w-3 h-3" /> {L("Gợi ý AI", "AI Suggested")} · {conf}%
              </span>
            )}
            {isFallback && (
              <span data-testid={`kbi-badge-fallback-${idx}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-semibold rounded-full px-2 py-0.5 bg-warmgray-100 text-warmgray-700">
                {L("Dự phòng", "Fallback")}
              </span>
            )}
            {lowConf && (
              <span data-testid={`kbi-badge-low-conf-${idx}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-semibold rounded-full px-2 py-0.5 bg-amber-100 text-amber-800">
                {L("Độ tin cậy thấp", "Low Confidence")}
              </span>
            )}
            {isUrgent && (
              <span data-testid={`kbi-badge-urgent-${idx}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-semibold rounded-full px-2 py-0.5 bg-rose-600 text-white">
                <AlertTriangle className="w-3 h-3" /> {L("Rà soát khẩn", "Urgent Review")}
              </span>
            )}
            {isHigh && !isUrgent && (
              <span data-testid={`kbi-badge-high-${idx}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-semibold rounded-full px-2 py-0.5 bg-orange-100 text-orange-800">
                <AlertTriangle className="w-3 h-3" /> {L("Ưu tiên cao", "High Priority")}
              </span>
            )}
            {(r.compliance_severity === "critical" || r.compliance_severity === "high") && (
              <span data-testid={`kbi-badge-risk-${idx}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-semibold rounded-full px-2 py-0.5 bg-rose-100 text-rose-800">
                {L("Nguy cơ cao", "High Risk")} · {r.compliance_severity}
              </span>
            )}
            {(prio === "high" || prio === "urgent") && (r.target_lifecycle === "under_review") && (
              <span data-testid={`kbi-badge-needs-review-${idx}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-semibold rounded-full px-2 py-0.5 bg-amber-100 text-amber-900">
                {L("Cần người duyệt", "Needs Human Review")}
              </span>
            )}
          </div>

          {r.classification_reasoning && (
            <p data-testid={`kbi-reasoning-${idx}`} className="mt-1.5 text-[11px] italic text-espresso-soft line-clamp-2">
              <Wand2 className="w-3 h-3 inline mr-1" />
              {r.classification_reasoning}
            </p>
          )}

          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 text-xs">
            <FieldMini label={L("Danh mục", "Category")} value={r.category} onChange={(v) => onChange({ category: v })} testId={`kbi-cat-${idx}`} />
            <FieldMini label={L("Phụ mục", "Subcat")} value={r.subcategory} onChange={(v) => onChange({ subcategory: v })} testId={`kbi-sub-${idx}`} />
            <FieldMini label={L("Phạm vi", "Juris.")} value={r.jurisdiction} onChange={(v) => onChange({ jurisdiction: v })} testId={`kbi-jur-${idx}`} />
            <SelectMini label={L("Mức độ", "Severity")} value={r.compliance_severity} onChange={(v) => onChange({ compliance_severity: v })} testId={`kbi-sev-${idx}`}
              options={[["none","none"],["low","low"],["medium","medium"],["high","high"],["critical","critical"]]} />
            <FieldMini label={L("Ngôn ngữ", "Lang")} value={r.language} onChange={(v) => onChange({ language: v })} testId={`kbi-lang-${idx}`} />
            <SelectMini label={L("Ưu tiên", "Priority")} value={r.review_priority} onChange={(v) => onChange({ review_priority: v })} testId={`kbi-prio-${idx}`}
              options={[["low","low"],["normal","normal"],["high","high"],["urgent","urgent"]]} />
            <SelectMini label={L("Lifecycle", "Lifecycle")} value={r.target_lifecycle} onChange={(v) => onChange({ target_lifecycle: v })} testId={`kbi-lc-${idx}`}
              options={[["draft","draft"],["under_review","under_review"],["approved","approved"],["published","published"],["needs_update","needs_update"],["archived","archived"]]} />
          </div>
          <div className="mt-2">
            <FieldMini label={L("Tags", "Tags")} value={typeof r.tags === "string" ? r.tags : (r.tags || []).join(", ")}
              onChange={(v) => onChange({ tags: v })} testId={`kbi-tags-${idx}`} />
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px]">
            {hasWarnings && (
              <span data-testid={`kbi-row-warn-${idx}`} className="inline-flex items-start gap-1 bg-amber-100 text-amber-900 rounded-lg px-2 py-1 max-w-full">
                <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                <span className="break-words">{(r.classification_warnings || []).join(" · ")}</span>
              </span>
            )}
            {hasDup && (
              <span data-testid={`kbi-row-dup-${idx}`} className="inline-flex items-center gap-1 bg-amber-100 text-amber-900 rounded-full px-2 py-0.5">
                <Copy className="w-3 h-3" />
                {L(`${r.duplicates.length} trùng`, `${r.duplicates.length} duplicates`)}: #{r.duplicates.map((d) => d.id).join(", #")}
              </span>
            )}
            {hasErr && (
              <span data-testid={`kbi-row-err-${idx}`} className="inline-flex items-center gap-1 bg-rose-100 text-rose-900 rounded-full px-2 py-0.5">
                <AlertTriangle className="w-3 h-3" /> {r.errors.join(" · ")}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldMini({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <div className="text-[9px] uppercase tracking-[0.14em] text-espresso-muted">{label}</div>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full bg-white border border-espresso/15 rounded-lg px-2 py-1 text-xs focus:border-espresso/40 focus:outline-none" />
    </label>
  );
}

function SelectMini({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <div className="text-[9px] uppercase tracking-[0.14em] text-espresso-muted">{label}</div>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full bg-white border border-espresso/15 rounded-lg px-2 py-1 text-xs focus:border-espresso/40 focus:outline-none">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}
